"use client";

/**
 * Per-document GET de-duplication for the public catalogue.
 *
 * Platform pages render both the hero package selector and the full catalogue.
 * They previously fired identical platform/category/sub-category requests at the
 * same time, doubling backend work and occasionally hitting the per-IP rate limit.
 * Successful responses are shared for one minute; errors are never cached, so a
 * temporary 429 or network failure can recover immediately on the next attempt.
 */
const CATALOG_CACHE_TTL_MS = 60_000;
const CATALOG_MAX_ATTEMPTS = 3;
const TRANSIENT_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

const responseCache = new Map<string, {
    expiresAt: number;
    response: Promise<Response>;
}>();

function retryDelayMs(response: Response | undefined, attempt: number): number {
    const retryAfter = response?.headers.get("retry-after");
    if (retryAfter) {
        const seconds = Number(retryAfter);
        if (Number.isFinite(seconds) && seconds >= 0) {
            return Math.min(3_000, Math.max(250, seconds * 1_000));
        }
    }
    return 350 * (attempt + 1);
}

async function requestCatalogue(url: string): Promise<Response> {
    let lastError: unknown;

    for (let attempt = 0; attempt < CATALOG_MAX_ATTEMPTS; attempt++) {
        let response: Response | undefined;
        try {
            response = await fetch(url, { headers: { Accept: "application/json" } });
            if (response.ok || !TRANSIENT_STATUS.has(response.status)) return response;

            // A failed response body is not consumed by the caller. Cancel it before
            // retrying so the browser can immediately reuse the connection.
            await response.body?.cancel().catch(() => undefined);
        } catch (error) {
            lastError = error;
        }

        if (attempt < CATALOG_MAX_ATTEMPTS - 1) {
            await new Promise((resolve) => setTimeout(resolve, retryDelayMs(response, attempt)));
        }
    }

    if (lastError) throw lastError;
    // The final response can only be absent when every fetch threw. Keep this
    // fail-closed instead of inventing an empty successful catalogue response.
    throw new Error(`Catalogue request failed after ${CATALOG_MAX_ATTEMPTS} attempts: ${url}`);
}

export async function cachedCatalogFetch(url: string): Promise<Response> {
    const now = Date.now();
    const cached = responseCache.get(url);
    if (cached && cached.expiresAt > now) {
        return (await cached.response).clone();
    }
    if (cached) responseCache.delete(url);

    const response = requestCatalogue(url)
        .then((res) => {
            if (!res.ok) responseCache.delete(url);
            return res;
        })
        .catch((error) => {
            responseCache.delete(url);
            throw error;
        });

    responseCache.set(url, {
        expiresAt: now + CATALOG_CACHE_TTL_MS,
        response,
    });

    return (await response).clone();
}
